import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlusCircle, Trash2 } from 'lucide-react';
import React from 'react';

const PLTPInspectionFields = ({ findings, setFindings }) => {
   // Initialize empty tree entry
   const emptyTreeEntry = {
      species: '',
      scientificName: '',
      numberOfTreesInventoried: 0,
      grossVolume: 0,
      netVolume: 0,
      treeStatus: 'Standing'
   };

   // Tree status options
   const treeStatusOptions = ['Standing', 'Blown Down'];

   // Add new tree entry
   const addTreeEntry = () => {
      setFindings(prev => ({
         ...prev,
         pltpFindings: {
            ...prev.pltpFindings || {},
            treeInventory: [...(prev.pltpFindings?.treeInventory || []), { ...emptyTreeEntry }]
         }
      }));
   };

   // Remove tree entry
   const removeTreeEntry = (index) => {
      // Prevent removing the last entry
      if (findings.pltpFindings?.treeInventory?.length <= 1) {
         return;
      }

      setFindings(prev => ({
         ...prev,
         pltpFindings: {
            ...prev.pltpFindings || {},
            treeInventory: prev.pltpFindings?.treeInventory.filter((_, i) => i !== index)
         }
      }));
      updateTotals();
   };

   // Update tree entry
   const updateTreeEntry = (index, field, value) => {
      setFindings(prev => {
         const newFindings = {
            ...prev,
            pltpFindings: {
               ...prev.pltpFindings || {},
               treeInventory: [...(prev.pltpFindings?.treeInventory || [])]
            }
         };

         newFindings.pltpFindings.treeInventory[index] = {
            ...newFindings.pltpFindings.treeInventory[index],
            [field]: field === 'species' || field === 'treeStatus' || field === 'scientificName' ? value : parseFloat(value) || 0
         };

         // Calculate totals
         const totals = newFindings.pltpFindings.treeInventory.reduce((acc, curr) => ({
            totalTrees: acc.totalTrees + (curr.numberOfTreesInventoried || 0),
            totalGrossVolume: acc.totalGrossVolume + (curr.grossVolume || 0),
            totalNetVolume: acc.totalNetVolume + (curr.netVolume || 0)
         }), { totalTrees: 0, totalGrossVolume: 0, totalNetVolume: 0 });

         newFindings.pltpFindings = {
            ...newFindings.pltpFindings,
            ...totals
         };

         return newFindings;
      });
   };

   return (
      <div className="space-y-6">
         <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
               <Label>Lot Number</Label>
               <Input
                  value={findings?.pltpFindings?.lotNumber || ''}
                  onChange={(e) => setFindings(prev => ({
                     ...prev,
                     pltpFindings: {
                        ...prev.pltpFindings,
                        lotNumber: e.target.value
                     }
                  }))}
                  placeholder="Enter lot number"
               />
            </div>
            <div>
               <Label>Original Certificate of Title No.</Label>
               <Input
                  value={findings?.pltpFindings?.octNumber || ''}
                  onChange={(e) => setFindings(prev => ({
                     ...prev,
                     pltpFindings: {
                        ...prev.pltpFindings,
                        octNumber: e.target.value
                     }
                  }))}
                  placeholder="Enter OCT number"
               />
            </div>
         </div>

         <div>
            <Label className="text-base font-semibold">Tree Inventory</Label>
            <Table>
               <TableHeader>
                  <TableRow>
                     <TableHead>Species</TableHead>
                     <TableHead>Scientific Name</TableHead>
                     <TableHead>Tree Status</TableHead>
                     <TableHead>No. Of trees inventoried</TableHead>
                     <TableHead>Gross Volume (cu.m.)</TableHead>
                     <TableHead>Net Volume (cu.m.)</TableHead>
                     <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
               </TableHeader>
               <TableBody>
                  {findings?.pltpFindings?.treeInventory?.map((tree, index) => (
                     <TableRow key={index}>
                        <TableCell>
                           <Input
                              value={tree.species || ''}
                              onChange={(e) => updateTreeEntry(index, 'species', e.target.value)}
                              placeholder="Enter species"
                           />
                        </TableCell>
                        <TableCell>
                           <Input
                              value={tree.scientificName || ''}
                              onChange={(e) => updateTreeEntry(index, 'scientificName', e.target.value)}
                              placeholder="Enter scientific name"
                           />
                        </TableCell>
                        <TableCell>
                           <Select
                              value={tree.treeStatus || 'Standing'}
                              onValueChange={(value) => updateTreeEntry(index, 'treeStatus', value)}
                           >
                              <SelectTrigger>
                                 <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                              <SelectContent>
                                 {treeStatusOptions.map((status) => (
                                    <SelectItem key={status} value={status}>
                                       {status}
                                    </SelectItem>
                                 ))}
                              </SelectContent>
                           </Select>
                        </TableCell>
                        <TableCell>
                           <Input
                              type="number"
                              value={tree.numberOfTreesInventoried || ''}
                              onChange={(e) => updateTreeEntry(index, 'numberOfTreesInventoried', e.target.value)}
                              placeholder="0"
                           />
                        </TableCell>
                        <TableCell>
                           <Input
                              type="number"
                              step="0.001"
                              value={tree.grossVolume || ''}
                              onChange={(e) => updateTreeEntry(index, 'grossVolume', e.target.value)}
                              placeholder="0.000"
                           />
                        </TableCell>
                        <TableCell>
                           <Input
                              type="number"
                              step="0.001"
                              value={tree.netVolume || ''}
                              onChange={(e) => updateTreeEntry(index, 'netVolume', e.target.value)}
                              placeholder="0.000"
                           />
                        </TableCell>
                        <TableCell>
                           <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeTreeEntry(index)}
                           >
                              <Trash2 className="h-4 w-4 text-red-500" />
                           </Button>
                        </TableCell>
                     </TableRow>
                  ))}
                  {/* Totals row */}
                  <TableRow className="font-bold">
                     <TableCell colSpan={3}>Total</TableCell>
                     <TableCell>{findings?.pltpFindings?.totalTrees || 0}</TableCell>
                     <TableCell>{findings?.pltpFindings?.totalGrossVolume?.toFixed(3) || '0.000'}</TableCell>
                     <TableCell>{findings?.pltpFindings?.totalNetVolume?.toFixed(3) || '0.000'}</TableCell>
                     <TableCell></TableCell>
                  </TableRow>
               </TableBody>
            </Table>

            <Button
               type="button"
               variant="outline"
               className="mt-4"
               onClick={addTreeEntry}
            >
               <PlusCircle className="h-4 w-4 mr-2" />
               Add Tree Entry
            </Button>
         </div>
      </div>
   );
};

export default PLTPInspectionFields;